import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FileSpreadsheet, Info, Printer, RotateCcw } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  computeCompound,
  scheduleToCSV,
  type CalcMode,
  type CompoundFrequency,
} from "@/lib/calculators/compoundInterest";
import { BD_SAVINGS_PRODUCTS, SAVINGS_DISCLAIMER } from "@/data/bdSavingsProducts";

/* ---------- Strings ---------- */

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Compound Interest Calculator", bn: "চক্রবৃদ্ধি সুদ ক্যালকুলেটর" },
  desc: {
    en: "Calculate compound interest with Bangladesh savings products — DPS, FDR, Sanchayapatra. See maturity amount, total interest, and year-by-year growth.",
    bn: "বাংলাদেশের সঞ্চয় পণ্য — ডিপিএস, এফডিআর, সঞ্চয়পত্রের জন্য চক্রবৃদ্ধি সুদ হিসাব করুন। মেয়াদপূর্তির পরিমাণ, মোট সুদ এবং বছরভিত্তিক প্রবৃদ্ধি দেখুন।",
  },

  mode: { en: "Calculation mode", bn: "হিসাবের ধরন" },
  modeLump: { en: "Lump sum", bn: "এককালীন" },
  modeRecurring: { en: "Recurring (DPS)", bn: "মাসিক (ডিপিএস)" },
  modeBoth: { en: "Both", bn: "উভয়" },

  product: { en: "BD product preset", bn: "বিডি পণ্য প্রিসেট" },
  rateNote: {
    en: "Indicative rate — verify with bank or post office before investing.",
    bn: "হার আনুমানিক — বিনিয়োগের আগে ব্যাংক বা ডাকঘরের সঙ্গে যাচাই করুন।",
  },

  principal: { en: "Initial amount (Principal)", bn: "প্রাথমিক বিনিয়োগ" },
  monthly: { en: "Monthly contribution", bn: "মাসিক জমা" },
  rate: { en: "Annual interest rate", bn: "বার্ষিক সুদের হার" },
  period: { en: "Time period", bn: "সময়কাল" },
  years: { en: "Years", bn: "বছর" },
  months: { en: "Months", bn: "মাস" },
  freq: { en: "Compounding frequency", bn: "চক্রবৃদ্ধির ফ্রিকোয়েন্সি" },

  freqAnnually: { en: "Annually", bn: "বার্ষিক" },
  freqSemi: { en: "Semi-annually", bn: "অর্ধবার্ষিক" },
  freqQuarterly: { en: "Quarterly", bn: "ত্রৈমাসিক" },
  freqMonthly: { en: "Monthly", bn: "মাসিক" },
  freqDaily: { en: "Daily", bn: "দৈনিক" },
  freqContinuously: { en: "Continuously", bn: "অবিরাম" },

  reset: { en: "Reset", bn: "রিসেট" },
  bnNumerals: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },

  maturity: { en: "Maturity amount", bn: "মেয়াদপূর্তির পরিমাণ" },
  invested: { en: "Total invested", bn: "মোট বিনিয়োগ" },
  earned: { en: "Total interest earned", bn: "অর্জিত মোট সুদ" },
  multiplier: { en: "Wealth multiplier", bn: "সম্পদ বৃদ্ধি (গুণ)" },

  growthTitle: { en: "Year-by-year growth", bn: "বছরভিত্তিক প্রবৃদ্ধি" },
  stackedTitle: { en: "Principal vs interest", bn: "মূলধন বনাম সুদ" },
  scheduleTitle: { en: "Year-by-year breakdown", bn: "বছরভিত্তিক বিবরণ" },

  year: { en: "Year", bn: "বছর" },
  opening: { en: "Opening", bn: "শুরুতে" },
  contributions: { en: "Contributions", bn: "জমা" },
  interest: { en: "Interest", bn: "সুদ" },
  closing: { en: "Closing", bn: "মেয়াদ শেষে" },

  downloadCsv: { en: "Download CSV", bn: "CSV ডাউনলোড" },
  print: { en: "Print / PDF", bn: "প্রিন্ট / PDF" },

  taxWarnTitle: { en: "Tax on interest income", bn: "সুদ আয়ের উপর কর" },
  taxWarnBody: {
    en: "Interest income above ৳5,00,000/year is generally taxable in Bangladesh. 5-Year Bangladesh Sanchayapatra interest currently has tax exemption up to ৳5,00,000 — verify the latest NBR rules before relying on this.",
    bn: "বছরে ৳৫,০০,০০০-এর বেশি সুদ আয় বাংলাদেশে সাধারণত করযোগ্য। ৫ বছর মেয়াদী বাংলাদেশ সঞ্চয়পত্রের সুদ বর্তমানে ৳৫,০০,০০০ পর্যন্ত করমুক্ত — সর্বশেষ এনবিআর নিয়ম যাচাই করুন।",
  },

  sensitivity: { en: "What if rate were…?", bn: "যদি হার হয়…?" },
  sensitivityHelp: {
    en: "Drag to see maturity at a different annual rate.",
    bn: "ভিন্ন বার্ষিক হারে মেয়াদপূর্তি দেখতে স্লাইডার সরান।",
  },
} as const;

/* ---------- SEO + FAQ ---------- */

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "What is compound interest?", bn: "চক্রবৃদ্ধি সুদ কী?" },
    a: {
      en: "Compound interest is interest calculated on both the original principal and the previously accumulated interest. Over time the balance grows exponentially because each period's interest itself earns interest.",
      bn: "চক্রবৃদ্ধি সুদ হলো সুদ যা শুধু মূলধনের উপর নয়, পূর্বে অর্জিত সুদের উপরও হিসাব করা হয়। সময়ের সঙ্গে সঙ্গে এটি সূচকীয় হারে বাড়ে কারণ প্রতিটি সময়ের সুদ নিজেই সুদ অর্জন করে।",
    },
  },
  {
    q: { en: "DPS vs FDR — which is better?", bn: "ডিপিএস বনাম এফডিআর — কোনটা ভালো?" },
    a: {
      en: "FDR is a single lump-sum deposit for a fixed tenure (typically 3 months to 3 years) — best when you already have the money. DPS is a monthly recurring deposit (typically 3–10 years) — best when you want to build savings from your salary. DPS rates are usually slightly lower than FDR.",
      bn: "এফডিআর হলো নির্দিষ্ট মেয়াদের (সাধারণত ৩ মাস থেকে ৩ বছর) এককালীন জমা — যখন আপনার কাছে পুরো টাকা আছে তখন উপযুক্ত। ডিপিএস হলো মাসিক জমা স্কিম (সাধারণত ৩–১০ বছর) — বেতন থেকে সঞ্চয় তৈরি করতে চাইলে ভালো। ডিপিএসের হার সাধারণত এফডিআরের চেয়ে সামান্য কম।",
    },
  },
  {
    q: { en: "How does DPS work in Bangladesh?", bn: "DPS কীভাবে কাজ করে?" },
    a: {
      en: "You commit to depositing a fixed amount (e.g. ৳5,000) into the bank every month for a fixed tenure (3, 5, 8 or 10 years). At maturity the bank returns your total deposits plus compound interest at the agreed rate. Missing instalments may forfeit interest or attract penalties.",
      bn: "আপনি নির্দিষ্ট মেয়াদের জন্য (৩, ৫, ৮ বা ১০ বছর) প্রতি মাসে নির্দিষ্ট পরিমাণ (যেমন ৳৫,০০০) ব্যাংকে জমা দেওয়ার অঙ্গীকার করেন। মেয়াদ শেষে ব্যাংক মোট জমা + চক্রবৃদ্ধি সুদ ফেরত দেয়। কিস্তি বাদ দিলে সুদ হারাতে পারেন বা জরিমানা হতে পারে।",
    },
  },
  {
    q: { en: "What is Sanchayapatra and who can buy it?", bn: "সঞ্চয়পত্র কী এবং কারা কিনতে পারে?" },
    a: {
      en: "Sanchayapatra (savings certificate) is a government-issued instrument sold through Bangladesh Post Office and authorised banks. Most types are reserved for individuals — adults, pensioners, or families. NID and TIN are required. There are individual purchase ceilings (typically ৳50 lakh single / ৳1 crore joint).",
      bn: "সঞ্চয়পত্র সরকার-ইস্যুকৃত এক ধরনের বিনিয়োগ যা ডাকঘর ও অনুমোদিত ব্যাংকে কেনা যায়। বেশির ভাগ ধরন শুধু ব্যক্তি — প্রাপ্তবয়স্ক, পেনশনার বা পরিবার-কেন্দ্রিক। NID ও TIN প্রয়োজন। ব্যক্তিগত ক্রয়সীমা থাকে (সাধারণত ৳৫০ লক্ষ একক / ৳১ কোটি যৌথ)।",
    },
  },
  {
    q: { en: "Does daily compounding really make a big difference?", bn: "দৈনিক চক্রবৃদ্ধি কি সত্যিই বড় পার্থক্য করে?" },
    a: {
      en: "Not as much as people think. At 8% over 10 years, daily vs annual compounding differs by ~4% in maturity. The bigger drivers of growth are the rate, time, and contributions — not the compounding cadence.",
      bn: "সাধারণত যতটা মনে করা হয়, ততটা নয়। ৮% হারে ১০ বছরে দৈনিক ও বার্ষিক চক্রবৃদ্ধির পার্থক্য মেয়াদপূর্তিতে প্রায় ৪%। প্রবৃদ্ধির প্রধান চালক হলো হার, সময় এবং নিয়মিত জমা — চক্রবৃদ্ধির ফ্রিকোয়েন্সি নয়।",
    },
  },
  {
    q: { en: "Is DPS interest taxed?", bn: "DPS-এর সুদের উপর কর কাটে?" },
    a: {
      en: "Yes. Banks deduct TDS (Tax Deducted at Source) on DPS interest at maturity — typically 10% if you have a TIN, 15% if you do not. Sanchayapatra interest is treated separately, with limited tax-exemption thresholds.",
      bn: "হ্যাঁ। ব্যাংক মেয়াদ শেষে DPS-এর সুদের উপর TDS (উৎসে কর) কাটে — TIN থাকলে সাধারণত ১০%, না থাকলে ১৫%। সঞ্চয়পত্রের সুদ আলাদা নিয়মে, সীমিত করমুক্তি সহ।",
    },
  },
  {
    q: { en: "Can I withdraw FDR before maturity?", bn: "মেয়াদের আগে কি FDR ভাঙানো যায়?" },
    a: {
      en: "Yes, but premature encashment usually attracts a penalty: you receive interest at a savings-account rate (typically 2–4%) instead of the contracted FDR rate, and may forfeit any interest accrued for the partial period.",
      bn: "হ্যাঁ, তবে মেয়াদের আগে ভাঙালে জরিমানা হয়: চুক্তিকৃত FDR হারের পরিবর্তে সাধারণ সঞ্চয়ী হিসাবের হারে (সাধারণত ২–৪%) সুদ পাবেন, এবং অসম্পূর্ণ সময়ের সুদ হারাতেও পারেন।",
    },
  },
  {
    q: { en: "What's the formula for compound interest?", bn: "চক্রবৃদ্ধি সুদের সূত্র কী?" },
    a: {
      en: "A = P × (1 + r/n)^(n × t). P = principal, r = annual rate (decimal), n = compounding periods per year, t = time in years. For continuous compounding: A = P × eʳᵗ.",
      bn: "A = P × (১ + r/n)^(n × t)। P = মূলধন, r = বার্ষিক হার (দশমিক), n = বছরে চক্রবৃদ্ধির সংখ্যা, t = বছরে সময়। অবিরাম চক্রবৃদ্ধির জন্য: A = P × eʳᵗ।",
    },
  },
  {
    q: { en: "How is recurring deposit (DPS) maturity calculated?", bn: "মাসিক জমার (DPS) মেয়াদপূর্তি কীভাবে হিসাব হয়?" },
    a: {
      en: "Each monthly contribution is treated as a small principal that compounds for the remaining months. The total is the sum of all those compounded contributions. The closed-form annuity-due formula is FV = PMT × [((1+i)ⁿ − 1) / i] × (1+i), where i is the periodic rate.",
      bn: "প্রতিটি মাসিক জমাকে ছোট মূলধন ধরে অবশিষ্ট মাসের জন্য চক্রবৃদ্ধি করা হয়। সব মিলিয়ে মোট হলো মেয়াদপূর্তির পরিমাণ। অ্যানুইটি-ডিউ সূত্র: FV = PMT × [((১+i)ⁿ − ১) / i] × (১+i), যেখানে i হলো প্রতি সময়ের সুদের হার।",
    },
  },
  {
    q: { en: "Which is the highest-interest savings instrument in BD?", bn: "বিডিতে সবচেয়ে বেশি সুদ কোন সঞ্চয়ে?" },
    a: {
      en: "Family/Pensioner Sanchayapatra typically offers the highest rate (~11.76%), followed by 5-Year Bangladesh Sanchayapatra (~11.04%). Bank FDRs and DPS yield 7–9.5%. Equity mutual funds can yield more on average but carry market risk.",
      bn: "পরিবার/পেনশনার সঞ্চয়পত্র সাধারণত সর্বোচ্চ (~১১.৭৬%), এরপর ৫ বছর মেয়াদী বাংলাদেশ সঞ্চয়পত্র (~১১.০৪%)। ব্যাংক FDR ও DPS-এ ৭–৯.৫%। ইক্যুইটি মিউচুয়াল ফান্ড গড়ে বেশি দিতে পারে তবে ঝুঁকি থাকে।",
    },
  },
];

/* ---------- Route ---------- */

export const Route = createFileRoute("/compound-interest-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Compound Interest Calculator — DPS, FDR, Sanchaypatra",
      description:
        "Free compound interest calculator with Bangladesh savings products: DPS, FDR, Sanchaypatra. Calculate maturity amount, total interest. কম্পাউন্ড ইন্টারেস্ট ক্যালকুলেটর।",
      path: "/compound-interest-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Compound Interest Calculator", path: "/compound-interest-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: CompoundInterestPage,
});

/* ---------- Page ---------- */

const PRINCIPAL_MIN = 0;
const PRINCIPAL_MAX = 100_000_000; // 10 crore
const MONTHLY_MIN = 0;
const MONTHLY_MAX = 500_000;
const RATE_MIN = 0.1;
const RATE_MAX = 30;

const FREQS: { id: CompoundFrequency; key: keyof typeof STR }[] = [
  { id: "annually", key: "freqAnnually" },
  { id: "semiannually", key: "freqSemi" },
  { id: "quarterly", key: "freqQuarterly" },
  { id: "monthly", key: "freqMonthly" },
  { id: "daily", key: "freqDaily" },
  { id: "continuously", key: "freqContinuously" },
];

function CompoundInterestPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));

  const [mode, setMode] = useState<CalcMode>("lumpsum");
  const [productId, setProductId] = useState<string>("custom");
  const [principalStr, setPrincipalStr] = useState("100000");
  const [monthlyStr, setMonthlyStr] = useState("5000");
  const [rateStr, setRateStr] = useState("8");
  const [periodUnit, setPeriodUnit] = useState<"years" | "months">("years");
  const [periodValStr, setPeriodValStr] = useState("10");
  const [freq, setFreq] = useState<CompoundFrequency>("annually");
  const [bnDigits, setBnDigits] = useState(false);
  const [sensitivityRate, setSensitivityRate] = useState<number>(8);

  const principal = clamp(parseFloat(principalStr) || 0, PRINCIPAL_MIN, PRINCIPAL_MAX);
  const monthly = clamp(parseFloat(monthlyStr) || 0, MONTHLY_MIN, MONTHLY_MAX);
  const rate = clamp(parseFloat(rateStr) || 0, 0, RATE_MAX);
  const periodVal = Math.max(1, parseFloat(periodValStr) || 0);
  const years = periodUnit === "years" ? periodVal : periodVal / 12;

  // Effective inputs — zero out fields not used by current mode
  const effPrincipal = mode === "recurring" ? 0 : principal;
  const effMonthly = mode === "lumpsum" ? 0 : monthly;

  const result = useMemo(
    () =>
      computeCompound({
        principal: effPrincipal,
        monthlyContribution: effMonthly,
        annualRatePct: rate,
        years,
        frequency: freq,
        contribTiming: "begin",
      }),
    [effPrincipal, effMonthly, rate, years, freq],
  );

  const sensitivityResult = useMemo(
    () =>
      computeCompound({
        principal: effPrincipal,
        monthlyContribution: effMonthly,
        annualRatePct: sensitivityRate,
        years,
        frequency: freq,
        contribTiming: "begin",
      }),
    [effPrincipal, effMonthly, sensitivityRate, years, freq],
  );

  // Initialise sensitivity slider when rate changes via preset
  useEffect(() => {
    setSensitivityRate(rate);
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const money = (v: number, frac = 0) => {
    const useBn = lang === "bn" || bnDigits;
    return formatBDT(Math.round(v * 100) / 100, useBn ? "bn" : "en", frac);
  };

  const applyProduct = (id: string) => {
    setProductId(id);
    const p = BD_SAVINGS_PRODUCTS.find((x) => x.id === id);
    if (!p) return;
    setRateStr(String(p.rate));
    setPeriodUnit("years");
    setPeriodValStr(String(p.defaultYears));
    setFreq(p.defaultFrequency);
    setMode(p.defaultMode);
    if (p.defaultPrincipal != null) setPrincipalStr(String(p.defaultPrincipal));
    if (p.defaultMonthly != null) setMonthlyStr(String(p.defaultMonthly));
  };

  const reset = () => {
    setMode("lumpsum");
    setProductId("custom");
    setPrincipalStr("100000");
    setMonthlyStr("5000");
    setRateStr("8");
    setPeriodUnit("years");
    setPeriodValStr("10");
    setFreq("annually");
  };

  const downloadCSV = () => {
    const csv = scheduleToCSV(result.yearly);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compound-interest-${rate}pc-${Math.round(years)}y.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Chart data — invested vs maturity per year
  const chartData = useMemo(() => {
    let invested = effPrincipal;
    return result.yearly.map((y) => {
      invested += y.contributions;
      return {
        year: y.year,
        invested: Math.round(invested),
        maturity: Math.round(y.closing),
        principal: Math.round(invested),
        interest: Math.max(0, Math.round(y.closing - invested)),
      };
    });
  }, [result.yearly, effPrincipal]);

  const showInterestWarn = result.totalInterest > 500_000;

  return (
    <CalculatorLayout
      slug="compound-interest-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="TrendingUp"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <TooltipProvider delayDuration={150}>
        {/* Mode toggle */}
        <div className="space-y-2">
          <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("mode")}</p>
          <UnitToggle<CalcMode>
            value={mode}
            onChange={setMode}
            options={[
              { value: "lumpsum", label: tr("modeLump") },
              { value: "recurring", label: tr("modeRecurring") },
              { value: "both", label: tr("modeBoth") },
            ]}
          />
        </div>

        {/* Product preset */}
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <label className={cn("flex items-center gap-1.5 text-sm font-medium", lang === "bn" && "font-bn")}>
              {tr("product")}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info size={13} className="text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className={cn(lang === "bn" && "font-bn")}>{tr("rateNote")}</TooltipContent>
              </Tooltip>
            </label>
            <Select value={productId} onValueChange={applyProduct}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BD_SAVINGS_PRODUCTS.map((p) => (
                  <SelectItem key={p.id} value={p.id} className={lang === "bn" ? "font-bn" : ""}>
                    {p.name[lang]}{p.id !== "custom" ? ` — ${p.rate}%` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("freq")}</label>
            <Select value={freq} onValueChange={(v) => setFreq(v as CompoundFrequency)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQS.map((f) => (
                  <SelectItem key={f.id} value={f.id} className={lang === "bn" ? "font-bn" : ""}>
                    {tr(f.key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Principal */}
        {mode !== "recurring" && (
          <div className="mt-6 space-y-3">
            <NumberInput label={tr("principal")} value={principalStr} onChange={setPrincipalStr} unit="৳" />
            <Slider
              value={[principal]}
              min={PRINCIPAL_MIN}
              max={PRINCIPAL_MAX}
              step={1000}
              onValueChange={(v) => setPrincipalStr(String(v[0]))}
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>{money(PRINCIPAL_MIN)}</span>
              <span>{money(PRINCIPAL_MAX)}</span>
            </div>
          </div>
        )}

        {/* Monthly */}
        {mode !== "lumpsum" && (
          <div className="mt-6 space-y-3">
            <NumberInput label={tr("monthly")} value={monthlyStr} onChange={setMonthlyStr} unit="৳" />
            <Slider
              value={[monthly]}
              min={MONTHLY_MIN}
              max={MONTHLY_MAX}
              step={500}
              onValueChange={(v) => setMonthlyStr(String(v[0]))}
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>{money(MONTHLY_MIN)}</span>
              <span>{money(MONTHLY_MAX)}</span>
            </div>
          </div>
        )}

        {/* Rate */}
        <div className="mt-6 space-y-3">
          <NumberInput label={tr("rate")} value={rateStr} onChange={setRateStr} unit="%" />
          <Slider
            value={[rate]}
            min={RATE_MIN}
            max={RATE_MAX}
            step={0.1}
            onValueChange={(v) => setRateStr(String(v[0]))}
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{num(RATE_MIN)}%</span>
            <span>{num(RATE_MAX)}%</span>
          </div>
        </div>

        {/* Period */}
        <div className="mt-6 grid gap-3 md:grid-cols-[1fr_auto]">
          <div>
            <NumberInput
              label={tr("period")}
              value={periodValStr}
              onChange={setPeriodValStr}
              unit={periodUnit === "years" ? tr("years") : tr("months")}
            />
            <Slider
              className="mt-3"
              value={[periodVal]}
              min={1}
              max={periodUnit === "years" ? 50 : 600}
              step={1}
              onValueChange={(v) => setPeriodValStr(String(v[0]))}
            />
          </div>
          <div className="self-end">
            <UnitToggle<"years" | "months">
              value={periodUnit}
              onChange={(u) => {
                if (u === periodUnit) return;
                if (u === "months") {
                  const m = Math.round(periodVal * 12);
                  setPeriodUnit("months");
                  setPeriodValStr(String(m));
                } else {
                  const y = Math.max(1, Math.round((periodVal / 12) * 10) / 10);
                  setPeriodUnit("years");
                  setPeriodValStr(String(y));
                }
              }}
              options={[
                { value: "years", label: tr("years") },
                { value: "months", label: tr("months") },
              ]}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            <input
              type="checkbox"
              checked={bnDigits}
              onChange={(e) => setBnDigits(e.target.checked)}
              className="h-3.5 w-3.5 accent-accent"
            />
            {tr("bnNumerals")}
          </label>
          <Button variant="outline" onClick={reset} className="gap-2">
            <RotateCcw size={14} />
            {tr("reset")}
          </Button>
        </div>

        {/* Results */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          <div className="grid gap-3 md:grid-cols-4">
            <ResultCard label={tr("maturity")} value={money(result.maturity)} accent />
            <ResultCard label={tr("invested")} value={money(result.totalInvested)} />
            <ResultCard label={tr("earned")} value={money(result.totalInterest)} />
            <ResultCard
              label={tr("multiplier")}
              value={`${(lang === "bn" ? toBengaliNumerals(result.wealthMultiplier.toFixed(2)) : result.wealthMultiplier.toFixed(2))}x`}
            />
          </div>

          {/* Tax warning card */}
          {showInterestWarn && (
            <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-4">
              <p className={cn("text-sm font-semibold text-yellow-600 dark:text-yellow-400", lang === "bn" && "font-bn")}>
                ⚠️ {tr("taxWarnTitle")}
              </p>
              <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("taxWarnBody")}</p>
            </div>
          )}

          {/* Line chart — invested vs maturity */}
          {chartData.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className={cn("mb-3 text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("growthTitle")}</h3>
              <div className="h-64">
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} />
                    <YAxis stroke="var(--muted-foreground)" fontSize={11} tickFormatter={(v) => compactBDT(v, lang)} />
                    <RTooltip
                      formatter={(v: number, n: string) => [money(v), n === "invested" ? tr("invested") : tr("maturity")]}
                      labelFormatter={(l) => `${tr("year")} ${num(l)}`}
                      contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                    />
                    <Line type="monotone" dataKey="invested" stroke="var(--muted-foreground)" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="maturity" stroke="var(--accent)" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 flex justify-center gap-4 text-xs">
                <Legend color="var(--muted-foreground)" label={tr("invested")} />
                <Legend color="var(--accent)" label={tr("maturity")} />
              </div>
            </div>
          )}

          {/* Stacked area — principal vs interest */}
          {chartData.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className={cn("mb-3 text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("stackedTitle")}</h3>
              <div className="h-56">
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} />
                    <YAxis stroke="var(--muted-foreground)" fontSize={11} tickFormatter={(v) => compactBDT(v, lang)} />
                    <RTooltip
                      formatter={(v: number, n: string) => [money(v), n === "principal" ? tr("invested") : tr("interest")]}
                      labelFormatter={(l) => `${tr("year")} ${num(l)}`}
                      contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                    />
                    <Area type="monotone" dataKey="principal" stackId="1" stroke="var(--muted-foreground)" fill="var(--muted-foreground)" fillOpacity={0.4} />
                    <Area type="monotone" dataKey="interest" stackId="1" stroke="var(--accent)" fill="var(--accent)" fillOpacity={0.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Sensitivity slider */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("sensitivity")}</h3>
              <span className={cn("tabular-nums text-sm font-semibold text-accent", lang === "bn" && "font-bn")}>
                {num(sensitivityRate.toFixed(1))}% → {money(sensitivityResult.maturity)}
              </span>
            </div>
            <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("sensitivityHelp")}</p>
            <Slider
              className="mt-3"
              value={[sensitivityRate]}
              min={RATE_MIN}
              max={RATE_MAX}
              step={0.1}
              onValueChange={(v) => setSensitivityRate(v[0])}
            />
          </div>

          {/* Year-by-year table */}
          <div className="rounded-xl border border-border bg-card p-3 md:p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("scheduleTitle")}</h3>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={downloadCSV} className="gap-1.5">
                  <FileSpreadsheet size={14} />
                  {tr("downloadCsv")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.print()} className="gap-1.5">
                  <Printer size={14} />
                  {tr("print")}
                </Button>
                <ShareButton title={`${tr("maturity")}: ${money(result.maturity)}`} />
              </div>
            </div>
            <div className="max-h-96 overflow-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80 text-xs uppercase tracking-wide text-muted-foreground backdrop-blur">
                  <tr>
                    <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("year")}</th>
                    <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("opening")}</th>
                    <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("contributions")}</th>
                    <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("interest")}</th>
                    <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("closing")}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.yearly.map((y) => (
                    <tr key={y.year} className="border-t border-border">
                      <td className="px-3 py-2 tabular-nums">{num(y.year)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(y.opening)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(y.contributions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(y.interest)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(y.closing)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {SAVINGS_DISCLAIMER[lang]}
            </p>
          </div>
        </motion.div>

        {/* Educational content */}
        <article className="mt-12 space-y-10">
          <Section title={lang === "en" ? "What is compound interest?" : "চক্রবৃদ্ধি সুদ কী?"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "Compound interest is interest calculated on both your original deposit and the interest already earned on it. Each compounding period the balance grows, and that larger balance is the base for the next round of interest. Over long horizons, this 'interest on interest' becomes the dominant driver of wealth — Einstein reportedly called it the eighth wonder of the world."
                : "চক্রবৃদ্ধি সুদ হলো সুদ যা আপনার মূলধন এবং পূর্বে অর্জিত সুদ — উভয়ের উপর হিসাব করা হয়। প্রতিটি সময়ের শেষে ব্যালান্স বড় হয়, এবং পরের সুদের ভিত্তি হয় সেই বড় ব্যালান্স। দীর্ঘ সময়ে এই 'সুদের উপর সুদ' সম্পদ বৃদ্ধির প্রধান চালক হয়ে ওঠে।"}
            </p>
          </Section>

          <Section title={lang === "en" ? "Compound interest formula" : "চক্রবৃদ্ধি সুদের সূত্র"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              <strong>A = P × (1 + r/n)^(n × t)</strong>
              <br />
              {lang === "en"
                ? "P = principal, r = annual rate (decimal), n = compounding periods per year, t = years. Example: ৳1,00,000 at 10% for 10 years compounded annually → 1,00,000 × (1.10)¹⁰ = ৳2,59,374."
                : "P = মূলধন, r = বার্ষিক হার (দশমিক), n = বছরে চক্রবৃদ্ধির সংখ্যা, t = বছর। উদাহরণ: ৳১,০০,০০০ ১০% হারে ১০ বছর বার্ষিক চক্রবৃদ্ধি → ১,০০,০০০ × (১.১০)¹⁰ = ৳২,৫৯,৩৭৪।"}
            </p>
          </Section>

          <Section title={lang === "en" ? "Daily vs monthly vs annual compounding" : "দৈনিক, মাসিক ও বার্ষিক চক্রবৃদ্ধির পার্থক্য"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "More frequent compounding gives a slightly higher return, but the gain is small. ৳1,00,000 at 8% for 10 years grows to ৳2,15,892 (annual), ৳2,21,964 (monthly), or ৳2,22,541 (daily). The contribution rate, time, and your discipline matter far more."
                : "ঘন ঘন চক্রবৃদ্ধি একটু বেশি রিটার্ন দেয়, কিন্তু পার্থক্য কম। ৳১,০০,০০০ ৮% হারে ১০ বছর — বার্ষিক ৳২,১৫,৮৯২, মাসিক ৳২,২১,৯৬৪, দৈনিক ৳২,২২,৫৪১। জমার হার, সময় এবং নিয়মিততা অনেক বেশি গুরুত্বপূর্ণ।"}
            </p>
          </Section>

          <Section title={lang === "en" ? "DPS — Deposit Pension Scheme (Bangladesh)" : "DPS — ডিপোজিট পেনশন স্কিম (বাংলাদেশ)"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "DPS is a monthly recurring deposit offered by virtually every BD bank — DBBL, BRAC, Sonali, Janata, City, EBL, Islami Bank, etc. You commit to depositing a fixed amount each month for a tenure of 3, 5, 8 or 10 years. The bank credits compound interest (typically 6.5–8% p.a., compounded monthly) and pays the maturity amount at the end. Missing instalments may forfeit interest or attract penalties — automate transfers if possible."
                : "DPS হলো প্রতি মাসে নির্দিষ্ট পরিমাণ জমা করার একটি স্কিম যা প্রায় সব বিডি ব্যাংক — ডিবিবিএল, ব্র্যাক, সোনালী, জনতা, সিটি, ইবিএল, ইসলামী ব্যাংক — অফার করে। মেয়াদ সাধারণত ৩, ৫, ৮ বা ১০ বছর। ব্যাংক চক্রবৃদ্ধি সুদ (সাধারণত ৬.৫–৮% বার্ষিক, মাসিক চক্রবৃদ্ধি) দেয় এবং মেয়াদ শেষে মেয়াদপূর্তির অর্থ পরিশোধ করে। কিস্তি বাদ দিলে সুদ হারাতে পারেন — সম্ভব হলে অটো-ট্রান্সফার করুন।"}
            </p>
          </Section>

          <Section title={lang === "en" ? "FDR — Fixed Deposit Receipt (Bangladesh)" : "FDR — ফিক্সড ডিপোজিট রিসিট"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "FDR is a one-time lump-sum deposit locked for a fixed tenure — common options are 3 months, 6 months, 1 year, 2 years, or 3 years. Rates currently range 7.5–9.5%, with longer tenures generally paying more. Premature encashment usually drops the rate to a savings-account level (2–4%) and may forfeit accrued interest. AIT (Advance Income Tax) is deducted at source on interest."
                : "FDR হলো নির্দিষ্ট মেয়াদের জন্য এককালীন আমানত — সাধারণত ৩ মাস, ৬ মাস, ১ বছর, ২ বছর বা ৩ বছর। বর্তমান হার ৭.৫–৯.৫%; দীর্ঘ মেয়াদে সাধারণত বেশি। মেয়াদের আগে ভাঙালে সঞ্চয়ী হিসাবের হার (২–৪%) নেমে আসে এবং অর্জিত সুদ হারাতে পারেন। সুদের উপর AIT (অগ্রিম আয়কর) উৎসে কাটা হয়।"}
            </p>
          </Section>

          <Section title={lang === "en" ? "Sanchayapatra — full guide" : "সঞ্চয়পত্র — পূর্ণ গাইড"} lang={lang}>
            <ul className={cn("list-disc space-y-2 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? (
                <>
                  <li><strong>5-Year Bangladesh Sanchayapatra</strong> — for any adult resident, current rate ~11.04%, interest paid at maturity (compounded semi-annually).</li>
                  <li><strong>3-Month Profit-Bearing Sanchayapatra</strong> — quarterly profit payout, ~11.04%, ideal for income seekers.</li>
                  <li><strong>Family Sanchayapatra</strong> — for women aged 18+, persons with disabilities, or anyone aged 65+. Rate ~11.52% with monthly profit.</li>
                  <li><strong>Pensioner Sanchayapatra</strong> — for retired government employees only, rate ~11.76% with quarterly profit.</li>
                  <li>Purchase ceilings: typically ৳50 lakh (single name) or ৳1 crore (joint). NID + TIN + bank account required.</li>
                  <li>Tax: interest below ৳5,00,000/year is exempt; above that, 10% TDS applies.</li>
                </>
              ) : (
                <>
                  <li><strong>৫ বছর মেয়াদী বাংলাদেশ সঞ্চয়পত্র</strong> — যেকোনো প্রাপ্তবয়স্ক বাসিন্দার জন্য, বর্তমান হার ~১১.০৪%, মেয়াদ শেষে সুদ (অর্ধবার্ষিক চক্রবৃদ্ধি)।</li>
                  <li><strong>৩ মাস অন্তর মুনাফা ভিত্তিক সঞ্চয়পত্র</strong> — ত্রৈমাসিক মুনাফা, ~১১.০৪%, নিয়মিত আয়ের জন্য আদর্শ।</li>
                  <li><strong>পরিবার সঞ্চয়পত্র</strong> — ১৮+ মহিলা, প্রতিবন্ধী, বা ৬৫+ বয়সীদের জন্য। হার ~১১.৫২%, মাসিক মুনাফা।</li>
                  <li><strong>পেনশনার সঞ্চয়পত্র</strong> — শুধু অবসরপ্রাপ্ত সরকারি কর্মচারীদের জন্য, হার ~১১.৭৬%, ত্রৈমাসিক মুনাফা।</li>
                  <li>ক্রয়সীমা: একক নামে ৳৫০ লক্ষ, যৌথ ৳১ কোটি। NID + TIN + ব্যাংক হিসাব প্রয়োজন।</li>
                  <li>কর: বছরে ৳৫,০০,০০০ পর্যন্ত সুদ করমুক্ত; এর বেশি হলে ১০% TDS।</li>
                </>
              )}
            </ul>
          </Section>

          <Section title={lang === "en" ? "Tax on interest income (TDS rules)" : "সুদ আয়ের উপর কর (TDS নিয়ম)"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "Banks deduct TDS on interest credited to FDR / DPS / savings accounts: 10% if you have a TIN, 15% if you don't. Sanchayapatra interest above ৳5 lakh/year is taxed at 10% TDS. Always claim TDS as adjustment in your annual income tax return."
                : "ব্যাংক FDR / DPS / সঞ্চয়ী হিসাবের সুদে TDS কাটে: TIN থাকলে ১০%, না থাকলে ১৫%। সঞ্চয়পত্রের ৫ লক্ষ টাকার বেশি সুদে ১০% TDS। বার্ষিক আয়কর রিটার্নে এই TDS সমন্বয় হিসেবে দাবি করুন।"}
            </p>
          </Section>
        </article>
      </TooltipProvider>
    </CalculatorLayout>
  );
}

/* ---------- Helpers ---------- */

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function compactBDT(value: number, lang: "en" | "bn"): string {
  const abs = Math.abs(value);
  let out: string;
  if (abs >= 10_000_000) out = `${(value / 10_000_000).toFixed(1)}Cr`;
  else if (abs >= 100_000) out = `${(value / 100_000).toFixed(1)}L`;
  else if (abs >= 1_000) out = `${(value / 1_000).toFixed(0)}K`;
  else out = String(Math.round(value));
  return lang === "bn" ? toBengaliNumerals(out) : out;
}

function ResultCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "rounded-xl border p-4",
        accent ? "border-accent/40 bg-accent/5" : "border-border bg-card",
      )}
    >
      <p className={cn("text-xs font-semibold uppercase tracking-wide", accent ? "text-accent" : "text-muted-foreground")}>
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold tabular-nums md:text-2xl">{value}</p>
    </motion.div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
